/**
 * Audio helpers for ElevenLabs Conversational AI PCM streaming.
 *  - Input:  mic → 16kHz mono PCM16 → base64
 *  - Output: base64 PCM16 16kHz mono → Web Audio playback with queue + flush
 */

export const b64ToBytes = (b64) => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

export const bytesToB64 = (bytes) => {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
};

/** Downsample Float32 audio to target sample rate, returning Float32Array. */
export const downsampleFloat32 = (input, inRate, outRate) => {
  if (outRate === inRate) return input;
  const ratio = inRate / outRate;
  const newLen = Math.round(input.length / ratio);
  const out = new Float32Array(newLen);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < newLen) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (
      let i = offsetBuffer;
      i < nextOffsetBuffer && i < input.length;
      i++
    ) {
      accum += input[i];
      count++;
    }
    out[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return out;
};

export const float32ToPCM16 = (float32) => {
  const buf = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(buf);
};

/** Playback queue for 16kHz PCM16 chunks. */
export class PcmPlayer {
  constructor(sampleRate = 16000) {
    this.sampleRate = sampleRate;
    this.ctx = null;
    this.nextTime = 0;
    this.sources = [];
    this.analyser = null;
    this.gainNode = null;
    this.mediaDest = null;
    this.mediaEl = null;
    this._volume = 1;
  }
  ensureCtx() {
    if (!this.ctx || this.ctx.state === "closed") {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: this.sampleRate,
      });
      this.gainNode = this.ctx.createGain();
      this.gainNode.gain.value = this._volume;
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 256;
      this.gainNode.connect(this.analyser);
      // Route through an HTMLAudioElement so mobile hardware volume buttons
      // control this stream. iOS treats WebAudio as "system" audio and Android
      // routes it to the notification/ringer channel unless the graph is
      // attached to a media element — this fixes both.
      try {
        this.mediaDest = this.ctx.createMediaStreamDestination();
        this.analyser.connect(this.mediaDest);
        this.mediaEl = new Audio();
        this.mediaEl.autoplay = true;
        this.mediaEl.playsInline = true;
        this.mediaEl.srcObject = this.mediaDest.stream;
        // Kick playback; browsers require this to happen after a user gesture.
        this.mediaEl.play().catch(() => {});

        // Android: hint the OS that this is media so the per-app volume slider
        // and lock-screen controls show Glitch nicely.
        try {
          if ("mediaSession" in navigator) {
            navigator.mediaSession.metadata = new window.MediaMetadata({
              title: "Glitch",
              artist: "Voice AI",
              album: "Live conversation",
            });
            navigator.mediaSession.playbackState = "playing";
          }
        } catch (_) {
          /* ignore */
        }
      } catch (_) {
        // Fallback: play directly through the audio graph destination.
        this.analyser.connect(this.ctx.destination);
      }
      this.nextTime = this.ctx.currentTime;
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  }
  setVolume(v) {
    const clamped = Math.max(0, Math.min(1, Number(v) || 0));
    this._volume = clamped;
    if (this.gainNode) this.gainNode.gain.value = clamped;
  }
  getVolume() {
    return this._volume;
  }
  enqueueBase64(b64) {
    this.ensureCtx();
    const bytes = b64ToBytes(b64);
    const view = new DataView(bytes.buffer);
    const floats = new Float32Array(bytes.length / 2);
    for (let i = 0; i < floats.length; i++) {
      floats[i] = view.getInt16(i * 2, true) / 32768;
    }
    const buffer = this.ctx.createBuffer(1, floats.length, this.sampleRate);
    buffer.getChannelData(0).set(floats);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.gainNode);
    const start = Math.max(this.ctx.currentTime, this.nextTime);
    src.start(start);
    this.nextTime = start + buffer.duration;
    this.sources.push(src);
    src.onended = () => {
      this.sources = this.sources.filter((s) => s !== src);
    };
  }
  flush() {
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch (_) {
        /* ignore */
      }
    });
    this.sources = [];
    if (this.ctx) this.nextTime = this.ctx.currentTime;
  }
  isPlaying() {
    return this.sources.length > 0;
  }
  getLevel() {
    if (!this.analyser) return 0;
    const arr = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(arr);
    let sum = 0;
    for (let i = 0; i < arr.length; i++) sum += arr[i];
    return sum / arr.length / 255;
  }
  close() {
    this.flush();
    try {
      if (this.mediaEl) {
        this.mediaEl.pause();
        this.mediaEl.srcObject = null;
      }
    } catch (_) {
      /* ignore */
    }
    try {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "none";
      }
    } catch (_) {
      /* ignore */
    }
    this.mediaEl = null;
    this.mediaDest = null;
    if (this.ctx && this.ctx.state !== "closed") {
      try {
        this.ctx.close();
      } catch (_) {
        /* ignore */
      }
    }
    this.ctx = null;
  }
}

/** Mic capture → 16kHz PCM16 base64 chunks via callback. Returns stop(). */
export const startMicCapture = async (onChunk, onLevel) => {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);

  const bufferSize = 2048;
  const processor = ctx.createScriptProcessor(bufferSize, 1, 1);
  const inputRate = ctx.sampleRate;
  processor.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    const down = downsampleFloat32(input, inputRate, 16000);
    const pcm = float32ToPCM16(down);
    onChunk(bytesToB64(pcm));
    if (onLevel) {
      const arr = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(arr);
      let sum = 0;
      for (let i = 0; i < arr.length; i++) sum += arr[i];
      onLevel(sum / arr.length / 255);
    }
  };
  source.connect(processor);
  processor.connect(ctx.destination);

  return () => {
    try {
      processor.disconnect();
      source.disconnect();
      stream.getTracks().forEach((t) => t.stop());
      ctx.close();
    } catch (_) {
      /* ignore */
    }
  };
};
